<script setup lang="ts">
import { useGalleryStore } from "@/store/gallery.store";
import { useSeoStore } from "@/store/seo.store";
import { onMounted, ref, computed, watch } from "vue";
import { useAuthStore } from "@/store/useAuthStore";
import Preloader from "@/components/Preloader.vue";
import { useRoute, useRouter } from "vue-router";
import kissmetrics from "@/services/metrics/kissmetrics";
import { useWorkspaceSync } from "@/composables/useWorkspaceSync";
import CrossSvg from "@/svgs/CrossSvg.vue";
import Button from "@/components/Button.vue";
const route = useRoute();
const router = useRouter();

const authStore = useAuthStore();
const galleryStore = useGalleryStore();
const seoStore = useSeoStore();
const loading = ref(true);
const showContextMenu = ref(import.meta.env.VITE_DISABLE_INSPECT);

// Workspace sync
const {
    showAgencyBlocker,
    activeAgency,
    currentAgency,
    hasBroadcastSupport,
    switchToActiveAgency,
    keepCurrentWorkspace,
} = useWorkspaceSync();

// Basic user info from authStore
const user = computed(() => authStore.user);

async function init() {
    loading.value = true;
    try {
        await authStore.fetchUser();

        if (authStore.user) {
            galleryStore.dispatchGetGalleryImages();
            await seoStore.dispatchGetSeoData();
        }

        // Initialize KISSmetrics after user is loaded
        if (user.value && user.value.email) {
            await kissmetrics(user.value);
        }
    } catch {
        // Silently handle auth errors (e.g., 401 on login page)
    } finally {
        loading.value = false;
    }
}

// Re-fetch agency-scoped data when agency changes (e.g., user switches agency)
watch(
    () => authStore.user?.agency?.id,
    (newAgencyId, oldAgencyId) => {
        if (newAgencyId && oldAgencyId && newAgencyId !== oldAgencyId) {
            seoStore.dispatchGetSeoData();
            galleryStore.dispatchGetGalleryImages();
        }
    }
);

onMounted(async () => {
    // Wait for router to be ready
    await router.isReady();

    // Don't fetch user data for not found and forbidden
    const publicRoutes = ['NotFound', 'forbidden'];
    if (!publicRoutes.includes(route.name as string)) {
        init();
    } else {
        loading.value = false;
    }
    if (showContextMenu.value == "true") {
        document.addEventListener("contextmenu", function (event) {
            event.preventDefault();
        });

        // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
        document.addEventListener("keydown", function (event) {
            if (
                event.key === "F12" ||
                (event.ctrlKey &&
                    event.shiftKey &&
                    (event.key === "I" ||
                        event.key === "J" ||
                        event.key === "C")) ||
                (event.ctrlKey && event.key === "U")
            ) {
                event.preventDefault();
            }
        });
    }
});
</script>

<template>
    <router-view v-if="!loading" />
    <Preloader v-else />

    <Teleport to="body">
        <div v-if="showAgencyBlocker" class="fixed z-[99999] top-0 left-0 bottom-0 right-0 bg-black/65 backdrop-blur-sm flex items-center justify-center p-4">
            <!-- White Message Box -->
            <div class="bg-white rounded-2xl max-w-md w-full text-center shadow-2xl overflow-hidden">
                <!-- Brand accent bar -->
                 <div class="bg-primary h-1 w-full"></div>

                <div class="p-8">
                    <!-- Icon -->
                    <div class="w-16 h-16 bg-primary-10 rounded-full flex items-center justify-center mx-auto mb-5">
                        <svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                        </svg>
                    </div>

                    <!-- Message -->
                    <h2 class="text-xl font-bold text-gray-900 mb-2">
                        Agency Switched
                    </h2>

                    <p class="text-gray-500 text-sm mb-6 leading-relaxed">
                        Please reload this page or click the button below to continue with the active agency.
                    </p>

                    <!-- Agency Info -->
                    <div class="bg-primary-10 border border-primary-20 rounded-xl p-5 mb-6 text-sm">
                        <table class="mx-auto">
                            <tbody>
                                <tr>
                                    <td class="text-gray-400 pb-3 text-left align-top text-nowrap pr-4">Previous Agency:</td>
                                    <td class="font-semibold text-gray-700 text-left pb-3 pl-2 align-top">{{ currentAgency?.name || 'Unknown' }}</td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 font-medium text-left align-top text-nowrap pr-4">Active Agency:</td>
                                    <td class="font-semibold text-primary text-left pl-2 align-top">{{ activeAgency?.name || 'Unknown' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-col gap-3">
                        <Button
                            :classes="'w-full justify-center py-3 text-base font-semibold'"
                            @click="switchToActiveAgency"
                        >
                            Switch to Active Agency
                        </Button>
                        <Button
                            @click="keepCurrentWorkspace"
                            :outline="true"
                        >
                            Keep Current Workspace
                        </Button>
                    </div>

                    <!-- Info Text -->
                    <p class="mt-4 text-xs text-gray-400">
                        You can continue working in your current workspace safely
                    </p>

                    <!-- Browser Compatibility Warning -->
                    <div v-if="!hasBroadcastSupport" class="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700">
                        ⚠️ Real-time sync unavailable in this browser. Please refresh manually.
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>
